import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';

const TheLearnCTA = () => {
    const sectionRef = useRef(null);
    const visualRef = useRef(null);
    const cursorRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const section = sectionRef.current;
        const visual = visualRef.current;

        const handleMouseMove = (e) => {
            const rect = section.getBoundingClientRect();
            cursorRef.current = {
                x: (e.clientX - rect.left) / rect.width - 0.5,
                y: (e.clientY - rect.top) / rect.height - 0.5
            };
        };

        section.addEventListener('mousemove', handleMouseMove);

        const ctx = gsap.context(() => {
            // Continuous subtle animation for the visual elements
            gsap.to('.orb', {
                duration: 4,
                scale: 1.1,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut",
                stagger: {
                    each: 0.5,
                    from: "random"
                }
            });

            // Parallax effect based on mouse
            gsap.ticker.add(() => {
                gsap.set(visual, {
                    x: cursorRef.current.x * 30,
                    y: cursorRef.current.y * 30,
                    rotationX: cursorRef.current.y * 10,
                    rotationY: cursorRef.current.x * 10,
                });
            });
        }, sectionRef);

        return () => {
            section.removeEventListener('mousemove', handleMouseMove);
            ctx.revert();
        };
    }, []);

    return (
        <section ref={sectionRef} className="home-section-tight overflow-hidden group">
            {/* Background Glows */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none opacity-[0.16]">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-slate-400/[0.08] rounded-full blur-[150px] mix-blend-screen" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] bg-cyan-200/[0.04] rounded-full blur-[96px] mix-blend-screen" />
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="flex flex-col items-center justify-between gap-10 sm:gap-12 lg:flex-row lg:gap-16">
                    <div className="max-w-xl text-center lg:text-left order-2 lg:order-1">
                        <span className="home-kicker mb-4 block">Interactive Learning</span>
                        <h2 className="mb-6 text-3xl font-bold leading-[1.08] tracking-tight text-white sm:text-4xl md:mb-8 md:text-6xl">
                            Learn the physics <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400">behind the beam.</span>
                        </h2>
                        <p className="mb-7 text-base font-light leading-relaxed text-gray-400 sm:text-lg md:mb-8 md:text-xl">
                            The site teaches more than isolated facts. It teaches how detector inference works, how measurements emerge from interactions with matter and fields, and how physics becomes understandable when learners are allowed to explore it directly rather than memorize it passively.
                        </p>
                        <div className="mb-8 flex flex-wrap justify-center gap-3 lg:justify-start md:mb-10">
                            <span className="home-chip">Guided discovery</span>
                            <span className="home-chip">Inquiry-based design</span>
                            <span className="home-chip">Intuition before formalism</span>
                        </div>
                        <Link 
                            to="/learn" 
                            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-4 font-bold text-black transition-all hover:scale-105 active:scale-95 group/btn sm:w-auto sm:px-10 sm:py-5"
                        >
                            Explore the Curriculum
                            <svg className="w-5 h-5 transition-transform group-hover/btn:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </Link>
                    </div>

                    <div className="relative order-1 aspect-square w-full max-w-[320px] perspective-[1000px] sm:max-w-[420px] lg:order-2 lg:max-w-[500px]">
                        <div ref={visualRef} className="absolute inset-0 flex items-center justify-center transform-style-3d">
                            {/* Interactive Atom/Particle Visual */}
                            <div className="orb absolute h-48 w-48 rounded-full border-2 border-white/5 sm:h-64 sm:w-64" />
                            <div className="orb absolute hidden h-40 w-40 rotate-45 rounded-full border-2 border-white/10 sm:block sm:h-48 sm:w-48" />
                            <div className="orb absolute h-24 w-24 -rotate-45 rounded-full border-2 border-cyan-400/20 sm:h-32 sm:w-32" />
                            
                            {/* Core Nucleus */}
                            <div className="absolute z-20 h-6 w-6 rounded-full bg-white shadow-[0_0_24px_rgba(255,255,255,0.7)] sm:h-8 sm:w-8 sm:shadow-[0_0_30px_rgba(255,255,255,0.8)]" />
                            
                            {/* Electrons/Particles */}
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="absolute inset-0 animate-spin" style={{ animationDuration: `${3 + i}s`, animationDirection: i % 2 === 0 ? 'normal' : 'reverse' }}>
                                    <div className="absolute top-0 left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-cyan-400 shadow-[0_0_12px_#22d3ee] sm:h-4 sm:w-4 sm:shadow-[0_0_15px_#22d3ee]">
                                        <div className={`absolute inset-x-0 bottom-full w-full rounded-full bg-gradient-to-t from-cyan-400/50 to-transparent blur-sm ${i === 2 ? 'hidden sm:block h-20' : 'h-14 sm:h-20'}`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TheLearnCTA;
